import * as accountActions from './accountReducer';
import Axios from 'axios';
import { push } from 'react-router-redux';
import { toastr } from 'react-redux-toastr';
import queryString from '../../../helpers/queryString';
import handleErrorResponse from '../../../helpers/handleErrorResponse';
import { get } from 'lodash';
import { ADD_RESOURCE } from '../../layout/redux/breadcrumb';

const defaultIncludes = ['organisation', 'organisation.accounts_contacts'];
const ACCEPTED = 202;
export const clearAccount = () => {
  return {
    type: accountActions.CLEAR_ACCOUNT,
  };
};

export const isSubmitting = (status) => {
  return {
    type: accountActions.IS_SUBMITTING,
    status,
  };
};

export const isGeneratingReport = (status) => {
  return {
    type: accountActions.IS_GENERATING_REPORT,
    status,
  };
};

export const isSuspending = (status) => {
  return {
    type: accountActions.IS_SUSPENDING,
    status,
  };
};

export const isReinstating = (status) => {
  return {
    type: accountActions.IS_REINSTATING,
    status,
  };
};

export const getSummaries = (includes = '') => {
  return (dispatch) => {
    let endpoint = 'accounts/ledger-entries/summary';
    endpoint += queryString(includes);

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: accountActions.SET_SUMMARIES,
        data: response.data.meta,
      });
      return true;
    });
  };
};

export const getAccount = (id, includes = defaultIncludes) => {
  return (dispatch) => {
    let endpoint = 'accounts/' + id;
    endpoint += queryString(includes);

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: accountActions.SET_ACCOUNT,
        data: response.data,
      });
      dispatch({
        type: ADD_RESOURCE,
        id: response.data.data.id,
        name: response.data.data.attributes.name,
      });
      return true;
    });
  };
};

export const getTransactions = (accountId, filters = '') => {
  return (dispatch) => {
    let endpoint =
      'accounts/' + accountId + '/payments-and-receipts' + (filters ? '?' + filters : '');

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: accountActions.SET_TRANSACTIONS,
        data: response.data,
      });
      return true;
    });
  };
};

export const getPremiums = (accountId, premiumType = 'net-premiums', filters = '') => {
  return (dispatch) => {
    dispatch({
      type: accountActions.SET_PREMIUM_TYPE,
      premiumType: premiumType,
    });

    let endpoint =
      'accounts/' + accountId + '/' + premiumType + (filters ? '?' + filters : '');

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: accountActions.SET_PREMIUMS,
        premiumType: premiumType,
        data: response.data,
        filters,
      });
      return true;
    });
  };
};

export const createAccount = (resource) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.post('accounts', resource)
      .then((response) => {
        dispatch(isSubmitting(false));
        dispatch(push('/accounts/ledger/' + response.data.data.id));
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'Account could not be created');
      });
  };
};

export const updateAccount = (resource, includes = defaultIncludes) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    const id = resource.data.id;
    let endpoint = 'accounts/' + id;
    endpoint += queryString(includes);

    return Axios.patch(endpoint, resource)
      .then(() => {
        dispatch(isSubmitting(false));
        dispatch(push('/accounts/ledger/' + id));
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'Account could not be updated');
      });
  };
};

export const updateAccountingPeriod = (resource, cb) => {
  return (dispatch) => {
    const id = resource.data.id;
    let endpoint =
      'accounts/accounting-periods/' +
      id +
      ':' +
      (resource.data.attributes.open === true ? 'unlock' : 'lock');
    return Axios.post(endpoint, resource)
      .then(() => {
        if (cb) {
          dispatch(cb());
        }
        toastr.success('Success', 'Accounting period updated');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Accounting period could not be updated');
      });
  };
};

export const suspendAccount = (id) => {
  return (dispatch) => {
    dispatch(isSuspending(true));
    return Axios.post('accounts/' + id + ':suspend', {
      data: {
        metadata: {
          send_email: false,
        },
      },
    })
      .then(() => {
        dispatch(getAccount(id)).then(() => {
          dispatch(isSuspending(false));
          toastr.success('Suspended', 'The account has now been suspended');
          return true;
        });
      })
      .catch((error) => {
        dispatch(isSuspending(false));
        handleErrorResponse(error, 'There was an error suspending this account');
      });
  };
};

export const reinstateAccount = (id) => {
  return (dispatch) => {
    dispatch(isReinstating(true));
    return Axios.post('accounts/' + id + ':reinstate', {
      data: {
        metadata: {
          send_email: false,
        },
      },
    })
      .then(() => {
        dispatch(getAccount(id)).then(() => {
          dispatch(isReinstating(false));
          toastr.success('Suspended', 'The account has now been re-instated');
          return true;
        });
        return true;
      })
      .catch((error) => {
        dispatch(isReinstating(false));
        handleErrorResponse(error, 'There was an error re-instating this account');
      });
  };
};

export const saveAccountMatching = (matchedValues, accountId, cb) => {
  return (dispatch, getState) => {
    let endpoint = 'accounts/' + accountId + ':match';
    return Axios.post(endpoint, matchedValues)
      .then(() => {
        toastr.success('Success', 'The premiums have been matched');

        dispatch(getTransactions(accountId));
        dispatch(getPremiums(accountId, getState().account.premiumType));

        if (cb) {
          cb();
        }
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Account could not be updated');
      });
  };
};

export const savePaymentsAndReceipts = (paymentsAndReceipts) => {
  return (dispatch) => {
    let endpoint = 'accounts/payments-and-receipts';
    return Axios.post(endpoint, paymentsAndReceipts)
      .then(() => {
        dispatch(push('/accounts/bookkeeping'));
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error saving transactions');
      });
  };
};

export const postJournal = (journal) => {
  return (dispatch) => {
    let endpoint = 'accounts/journals?approve=true';
    return Axios.post(endpoint, journal)
      .then(() => {
        dispatch(push('/accounts/bookkeeping'));
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error saving transactions');
      });
  };
};

export const postAdjustment = (values, resource) => {
  return (dispatch) => {
    const accountId = resource.data.id;
    let endpoint = 'accounts/' + accountId + '/transaction-adjustment';
    return Axios.post(endpoint, values)
      .then(() => {
        dispatch(getTransactions(accountId));
        toastr.success('Success', 'Your adjustment has been saved');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error saving transactions');
      });
  };
};

export const makePayment = (values, resource) => {
  return () => {
    const accountId = resource.data.id;
    let endpoint = 'accounts/' + accountId + ':payment';

    return Axios.post(endpoint, values)
      .then(() => {
        toastr.success('Success', 'Your payment has been saved');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error saving payment');
      });
  };
};

export const markAsPaid = (brokerId, premiumId) => {
  return () => {
    let endpoint = `accounts/${brokerId}/${premiumId}:mark-as-paid`;

    return Axios.post(endpoint)
      .then(() => {
        toastr.success('Success', 'Premium marked as paid');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error saving payment');
      });
  };
};

export const generateReport = (values) => {
  const filterKeys = Object.keys(values);
  const filters = filterKeys
    .reduce((carry, filterKey) => {
      const value = values[filterKey];
      if (filterKey === 'generator') {
        if (value) {
          carry.push(`${filterKey}=queue`);
        }
        return carry;
      }
      carry.push(`params[${filterKey}]=${encodeURIComponent(value)}`);
      return carry;
    }, [])
    .join('&');

  return (dispatch) => {
    dispatch(isGeneratingReport(true));
    return Axios.get('reports?' + filters)
      .then((response) => {
        dispatch(isGeneratingReport(false));

        if (response.status === ACCEPTED) {
          toastr.success(
            'The report request has been submitted and you will be notified once it has completed',
          );
        } else if (window && response.data.meta.file_name) {
          window.open(response.data.meta.file_name, '_blank');
        }
      })
      .catch((error) => {
        dispatch(isGeneratingReport(false));
        handleErrorResponse(error, 'Error generating report');
      });
  };
};

export const viewStatement = (id) => {
  return () => {
    return Axios.get('accounts/' + id + '/statement')
      .then((response) => {
        if (window && response.data.meta.file_name) {
          window.open(response.data.meta.file_name, '_blank');
        }
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error generating statement');
      });
  };
};

export const getLedgerEntry = (id, includes = ['account']) => {
  return (dispatch) => {
    let endpoint = 'accounts/ledger-entries/' + id;
    endpoint += queryString(includes);

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: accountActions.SET_LEDGER_ENTRY,
        data: response.data,
      });
      return true;
    });
  };
};

export const unmatchBatch = (id, reason) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.delete('accounts/batches/' + id)
      .then(() => {
        dispatch(isSubmitting(false));
        toastr.success('Success', 'Batch has been un-matched');

        if (get(reason, 'data', false)) {
          return Axios.post('accounts/batches/' + id + '/notes', reason)
            .then(() => {
              return true;
            })
            .catch((error) => {
              handleErrorResponse(error, 'Error saving note');
            });
        }

        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'Error un-matching');
      });
  };
};

export const isCommissionRecalculating = (status) => {
  return {
    type: accountActions.IS_COMMISSION_RECALCULATING,
    status,
  };
};

export const setCommissionReallocation = (data) => {
  return {
    type: accountActions.SET_COMMISSION_REALLOCATION,
    data,
  };
};
export const recalculateCommissions = (id, commissions) => {
  return (dispatch) => {
    dispatch(isCommissionRecalculating(true));
    return Axios.post(
      'accounts/commissions/premiums/' + id + ':recalculate',
      commissions,
    ).then((res) => {
      dispatch(setCommissionReallocation(res.data));
      dispatch(isCommissionRecalculating(false));
    });
  };
};

export const reallocateCommissions = (id, commissions) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.post('accounts/commissions/premiums/' + id + ':reallocate', commissions)
      .then(() => {
        dispatch(isSubmitting(false));
        toastr.success('Success', 'Commission reallocation has been posted');
        return true;
      })
      .catch((error) => {
        dispatch(isSubmitting(false));
        handleErrorResponse(error, 'Error reallocating commission');
      });
  };
};

export const clearCommissionReallocation = () => {
  return {
    type: accountActions.CLEAR_COMMISSION_REALLOCATION,
  };
};

export const manualTransactionUpdate = (transactionId, values) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.post(`payments/${transactionId}:manual-transaction`, values)
      .then((resp) => {
        toastr.success('Success', 'Transaction Status Updated');
        return resp.data;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error updating transaction');
      });
  };
};

export const queryTransaction = (transactionId) => {
  return (dispatch) => {
    dispatch(isSubmitting(true));
    return Axios.get(`payments/transaction/${transactionId}:info`).then((resp) => {
      return resp.data;
    });
  };
};

export const actions = {
  manualTransactionUpdate,
  queryTransaction,
};
